import { useState, useRef, useEffect } from "react";
import { CheckCircle2, XCircle, Info, ScanLine } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useWarehouses, useAddSkuLocation } from "@/hooks/use-warehouse";
import { WarehouseNav } from "@/components/warehouse/warehouse-nav";
import { toast } from "sonner";
import { playSuccess, playError, playDuplicate } from "@/lib/sounds";
import { normalizeScannedBinCode } from "@/lib/warehouse/bin-label";
import { isolateScannerKeys } from "@/hooks/use-scanner-input";

type ScanEntry = {
  sku: string;
  binCode: string;
  timestamp: Date;
  success: boolean;
  duplicate?: boolean;
  error?: string;
};

const STORAGE_KEY = "komoran_warehouse_id";

export function ScanPage() {
  const { data: warehouses, isLoading } = useWarehouses();
  const addLocation = useAddSkuLocation();

  const [warehouseId, setWarehouseId] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [sku, setSku] = useState("");
  const [binCode, setBinCode] = useState("");
  const [recentScans, setRecentScans] = useState<ScanEntry[]>([]);
  const skuRef = useRef<HTMLInputElement>(null);
  const binRef = useRef<HTMLInputElement>(null);

  const warehouseValid = !!warehouses?.some((w: any) => w.id === warehouseId);

  useEffect(() => {
    if (warehouseId) localStorage.setItem(STORAGE_KEY, warehouseId);
  }, [warehouseId]);

  useEffect(() => {
    skuRef.current?.focus();
  }, []);

  useEffect(() => {
    if (warehouseId) setTimeout(() => skuRef.current?.focus(), 0);
  }, [warehouseId]);

  function handleSkuKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== "Enter") return;
    e.preventDefault();

    const trimSku = e.currentTarget.value.trim();
    if (!trimSku) return;

    setSku(trimSku);
    binRef.current?.focus();
  }

  async function handleBinKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== "Enter") return;
    e.preventDefault();

    const trimSku = sku.trim();
    const trimBin = normalizeScannedBinCode(e.currentTarget.value);
    if (!trimSku || !trimBin || !warehouseId) return;

    setBinCode(trimBin);
    await submitScan({ sku: trimSku, binCode: trimBin });
  }

  async function submitScan(input?: { sku: string; binCode: string }) {
    if (!warehouseValid) {
      toast.error("Odaberite skladište prije dodavanja lokacije");
      playError();
      return;
    }

    const trimSku = input?.sku ?? sku.trim();
    const trimBin = normalizeScannedBinCode(input?.binCode ?? binCode);

    if (trimBin.length > 64) {
      toast.error("Kod lokacije može imati najviše 64 znaka");
      playError();
      return;
    }

    try {
      const res = await addLocation.mutateAsync({
        sku: trimSku,
        binCode: trimBin,
        warehouseId,
      });

      if (res.duplicate) {
        playDuplicate();
        toast.info("Lokacija već postoji");
        setRecentScans((prev) => [
          { sku: trimSku, binCode: trimBin, timestamp: new Date(), success: true, duplicate: true },
          ...prev.slice(0, 19),
        ]);
      } else {
        playSuccess();
        toast.success(`${trimSku} → ${trimBin}`);
        setRecentScans((prev) => [
          { sku: trimSku, binCode: trimBin, timestamp: new Date(), success: true },
          ...prev.slice(0, 19),
        ]);
      }
      setSku("");
      setBinCode("");
      skuRef.current?.focus();
    } catch (err: any) {
      playError();
      toast.error(err.message);
      setRecentScans((prev) => [
        { sku: trimSku, binCode: trimBin, timestamp: new Date(), success: false, error: err.message },
        ...prev.slice(0, 19),
      ]);
    }
  }

  return (
    <div className="flex flex-col h-full">
      <WarehouseNav />

      <div className="p-4 flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label>Skladište</Label>
          {isLoading ? (
            <p className="text-xs text-muted-foreground">Učitavanje...</p>
          ) : (
            <Select value={warehouseId} onValueChange={setWarehouseId}>
              <SelectTrigger className="w-full sm:w-64 h-9!">
                <SelectValue placeholder="Odaberi skladište" />
              </SelectTrigger>
              <SelectContent>
                {warehouses?.map((w: any) => (
                  <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {warehouseId && (
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex flex-col gap-1.5 flex-1">
              <Label>SKU</Label>
              <div className="relative">
                <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  ref={skuRef}
                  value={sku}
                  onChange={(e) => setSku(e.target.value)}
                  onKeyDown={isolateScannerKeys(handleSkuKeyDown)}
                  placeholder="Skeniraj ili unesi SKU"
                  className="h-11 pl-9 text-sm"
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5 flex-1 sm:max-w-48">
              <Label>Lokacija</Label>
              <Input
                ref={binRef}
                value={binCode}
                onChange={(e) => setBinCode(e.target.value)}
                onKeyDown={isolateScannerKeys(handleBinKeyDown)}
                placeholder="npr. Server-1-2, A1, 1-2-3"
                className="h-11 text-sm"
                autoComplete="off"
              />
            </div>
          </div>
        )}

        {recentScans.length > 0 && (
          <div className="flex flex-col gap-1.5 mt-1">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Zadnja skeniranja</span>
            <div className="ring-1 ring-foreground/10 overflow-hidden">
              <div className="flex flex-col max-h-64 overflow-y-auto">
                {recentScans.map((entry, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 py-2 px-3 border-b last:border-0 text-xs"
                  >
                    {entry.success ? (
                      entry.duplicate ? (
                        <Info className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      ) : (
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" />
                      )
                    ) : (
                      <XCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
                    )}
                    <span className="font-medium">{entry.sku}</span>
                    <span className="text-muted-foreground">→</span>
                    <span>{entry.binCode}</span>
                    {entry.error && <span className="text-destructive truncate">— {entry.error}</span>}
                    <span className="text-muted-foreground ml-auto tabular-nums shrink-0">
                      {entry.timestamp.toLocaleTimeString("hr")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
